import { toast } from "sonner";
import { generateClient } from "./aws-generateClient-singleton";

export const executeGraphQLMutation = async ({
  query,
  variables = {},
  client = null,
  dispatch = null,
  dispatchFn = null,
  setLoading,
  showToast,
  signal,
}: {
  query: string;
  variables?: any
  client?: any;
  dispatch?: any;
  dispatchFn?: any;
  setLoading?: (loading: boolean) => void;
  showToast?: boolean;
  signal?: AbortSignal;
}) => {
  try {
    if (setLoading) setLoading(true);
    const graphqlClient = client || generateClient();

    const response = await graphqlClient.graphql({
      query: query,
      variables: { ...variables },
      ...(signal ? { abortSignal: signal } : {}),
    });

    if (response?.data) {
      const queryName = Object.keys(response.data)[0];
      return response.data[queryName];
    }
    if (dispatch && dispatchFn) {
      dispatch(dispatchFn(response));
    }
  } catch (error: any) {
    if (showToast) {
      toast.error(error.message || "An error occurred while fetching data.");
    }
    console.error("GraphQL mutation error:", error);
    return false;
  } finally {
    if (setLoading) setLoading(false);
  }
};

export const fetchAllWithPagination = async ({
  query,
  variables = {},
  client = null,
  setLoader,
  dispatch = null,
  dispatchFn,
  showToast = false,
  signal,
}: {
  query: string;
  variables?: Record<string, any>;
  client?: any;
  setLoader?: (loading: boolean) => void;
  dispatch?: any;
  dispatchFn?: any;
  showToast?: boolean;
  signal?: AbortSignal;
}) => {
  try {
    if (setLoader) setLoader(true);
    const graphqlClient = client || generateClient();
    let allItems: any[] = [];
    let nextToken = null;

    do {
      if (signal?.aborted) {
        break;
      }

      const response: any = await graphqlClient.graphql({
        query: query,
        variables: { ...variables, nextToken },
        ...(signal ? { abortSignal: signal } : {}),
      });

      const queryName = Object.keys(response.data)[0];
      const items = response.data[queryName].items;

      allItems = [...allItems, ...items];
      nextToken = response.data[queryName].nextToken;

      if (signal?.aborted) {
        break;
      }
    } while (nextToken);

    return allItems;
  } catch (error: any) {
    if (showToast) {
      toast.error(error.message || "An error occurred while fetching data.");
    }
    console.error("Error fetching data with pagination:", error);
    return [];
  } finally {
    if (setLoader && !signal?.aborted) {
      setLoader(false);
    }
  }
};

export const fetchWithCustomPagination = async ({
  query,
  variables,
  client,
  showToast = true,
  signal,
}: {
  query: any;
  variables: any;
  client: any;
  showToast?: boolean;
  signal?: AbortSignal;
}) => {
  try {
    const response = await client.graphql({
      query,
      variables,
      ...(signal ? { abortSignal: signal } : {}),
    });

    const queryName = Object.keys(response.data)[0];

    return {
      items: response.data[queryName].items,
      nextToken: response.data[queryName].nextToken,
      previousTokens: [],
    };
  } catch (error: any) {
    if (showToast) {
      toast.error(error.message || "An error occurred while fetching data.");
    }
    console.error("Error in custom pagination:", error);
    return { items: [], nextToken: null, previousTokens: [] };
  }
};
